import React, { useContext, useState } from "react";

import {
  Grid,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";

import toast from "react-hot-toast";

import { useExportSurvey } from "../../../store";
import { AuthContext } from "../../../context";

type AdminSurveyExportTabProps = {
  selectedClientId?: string;
};

const AdminSurveyExportTab: React.FC<AdminSurveyExportTabProps> = ({
  selectedClientId,
}) => {
  const { clientId } = useContext(AuthContext);
  const effectiveClientId = selectedClientId || clientId;

  const formatDateForInput = (date: Date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const today = new Date();
  const twelveMonthsAgo = new Date(today);
  twelveMonthsAgo.setMonth(today.getMonth() - 12);

  const [startDate, setStartDate] = useState<string>(() => formatDateForInput(twelveMonthsAgo));
  const [endDate, setEndDate] = useState<string>(() => formatDateForInput(today));

  const [genStartDate, setGenStartDate] = useState<string>(startDate);
  const [genEndDate, setGenEndDate] = useState<string>(endDate);
  const [genClientId, setGenClientId] = useState<string>(effectiveClientId);

  const [surveyData, setSurveyData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const startDateObj = startDate ? new Date(startDate) : undefined;
  const endDateObj = endDate ? new Date(endDate) : undefined;

  const dataIsDirty = () => {
    if (!surveyData) {
      return true;
    }

    return (
      genStartDate != startDate ||
      genEndDate != endDate ||
      genClientId != effectiveClientId
    );
  };

  const needsGeneration = dataIsDirty();

  const onSuccess = (csv: string) => {
    setSurveyData(csv);
    setGenStartDate(startDate);
    setGenEndDate(endDate);
    setGenClientId(effectiveClientId);
    toast.success("Survey data ready for download");
    setIsLoading(false);
  };

  const onFailure = (error: any) => {
    setSurveyData(null);
    console.error("Failed to get survey", error);
    toast.error("Failed to get survey data");
    setIsLoading(false);
  };

  const { mutate: getSurveyData } = useExportSurvey(onSuccess, onFailure);

  const generateSurveyData = () => {
    setIsLoading(true);
    getSurveyData({
      startRange: startDateObj,
      endRange: endDateObj,
      clientId: effectiveClientId,
    });
  };

  const handleDownloadSurvey = () => {
    if (!surveyData) {
      generateSurveyData();
      return;
    }

    const blob = new Blob([surveyData], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `survey-export-${startDate || "all"}-to-${endDate || "all"}.json`;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Stack direction="column" spacing={2} sx={{ p: 1 }}>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Select a date range to export survey data (leave empty for all data)
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
        </Grid>
      </Stack>

      <Stack>
        {needsGeneration ? (
          <Button
            variant="contained"
            fullWidth
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={generateSurveyData}
            disabled={isLoading}
            sx={{
              backgroundColor: "#055480",
              "&:hover": {
                backgroundColor: "#043e60",
              },
            }}
          >
            {isLoading ? "Generating Export..." : "Generate Export"}
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadSurvey}
            disabled={isLoading}
            sx={{
              backgroundColor: "#055480",
              "&:hover": {
                backgroundColor: "#043e60",
              },
            }}
          >
            {isLoading ? "Generating Export..." : "Download Survey Data"}
          </Button>
        )}
      </Stack>

      {surveyData && !isLoading && (
        <>
          {needsGeneration && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Survey data needs to be generated for new date range
            </Typography>
          )}

          {!needsGeneration && (
            <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
              Survey data ready for download
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
};

export default AdminSurveyExportTab;